import { useState } from "react";
import Error from "@mui/icons-material/Error";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Info from "@mui/icons-material/Info";
import Warning from "@mui/icons-material/Warning";
import type { Theme } from "@mui/material";
import { Box, Collapse, Stack, Typography, useTheme } from "@mui/material";

import type {
  TranslationKey,
  TranslationKeyAndOptions,
} from "@thalamos/vision-common";
import { buildTranslationInput } from "@thalamos/vision-common";

import { Translation } from "../Translation/Translation.js";
import { ExpandMore } from "./ExpandMore.js";

export type BannerList = "info" | "warning" | "error";

interface BannerProps {
  bannerType: BannerList;
  title?: TranslationKey | TranslationKeyAndOptions;
  body:
    | TranslationKey
    | TranslationKey[]
    | TranslationKeyAndOptions
    | TranslationKeyAndOptions[];
  expandable?: boolean;
}

const bannerTypeMapping = {
  ["error"]: {
    borderColor: (theme: Theme) => theme.palette.error.dark,
    backgroundColor: (theme: Theme) => theme.palette.error.light,
    icon: (theme: Theme) => (
      <Error
        sx={{
          color: theme.palette.error.dark,
          [theme.breakpoints.up("sm")]: {
            fontSize: "3rem",
          },
          [theme.breakpoints.down("sm")]: {
            fontSize: "1.5rem",
          },
        }}
      />
    ),
  },
  ["warning"]: {
    borderColor: (theme: Theme) => theme.palette.warning.main,
    backgroundColor: (theme: Theme) => theme.palette.warning.light,
    icon: (theme: Theme) => (
      <Warning
        sx={{
          color: theme.palette.warning.main,
          [theme.breakpoints.up("sm")]: {
            fontSize: "3rem",
          },
          [theme.breakpoints.down("sm")]: {
            fontSize: "1.5rem",
          },
        }}
      />
    ),
  },
  ["info"]: {
    borderColor: (theme: Theme) => theme.palette.primary.main,
    backgroundColor: (theme: Theme) => theme.palette.secondary.light,
    icon: (theme: Theme) => (
      <Info
        sx={{
          color: theme.palette.primary.main,
          [theme.breakpoints.up("sm")]: {
            fontSize: "3rem",
          },
          [theme.breakpoints.down("sm")]: {
            fontSize: "1.5rem",
          },
        }}
      />
    ),
  },
};

const getBannerProperties = (bannerType: BannerList, theme: Theme) => {
  const { borderColor, backgroundColor, icon } = bannerTypeMapping[bannerType];
  return {
    borderColor: borderColor(theme),
    backgroundColor: backgroundColor(theme),
    icon: icon(theme),
  };
};

export function Banner({ title, body, bannerType, expandable }: BannerProps) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);

  // We will put the body in different places depending on whether other features are enabled
  const bodyInHeader = !title;
  const bodyElement = (
    <Box
      sx={{
        whiteSpace: "pre-line",
      }}
    >
      {[body].flat().map((translationKeyAndOptions, index) => (
        <Translation
          key={index}
          {...buildTranslationInput(translationKeyAndOptions)}
        />
      ))}
    </Box>
  );

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const { borderColor, backgroundColor, icon } = getBannerProperties(
    bannerType,
    theme,
  );
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        flexWrap: "wrap",
        flexDirection: "column",
        borderLeft: `6px solid ${borderColor}`,
        borderRadius: "6px",
        backgroundColor: backgroundColor,
        padding: theme.spacing(2),
        userSelect: expandable ? "none" : "auto",
      }}
      onClick={expandable ? handleExpandClick : undefined}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: bodyInHeader ? "flex-start" : "center",
          justifyContent: "flex-start",
        }}
      >
        {icon}
        {title && (
          <>
            {expandable && (
              <ExpandMore
                expand={expanded}
                aria-expanded={expanded}
                aria-label="show more"
              >
                <ExpandMoreIcon />
              </ExpandMore>
            )}
            <Typography sx={{ color: "black", fontWeight: 600 }}>
              <Translation {...buildTranslationInput(title)} />
            </Typography>
          </>
        )}
        {bodyInHeader ? bodyElement : null}
      </Stack>
      {!bodyInHeader && (
        <Collapse
          in={expanded || !expandable || !title}
          timeout="auto"
          unmountOnExit
          sx={{
            paddingTop: 2,
            [theme.breakpoints.up("sm")]: {
              paddingLeft: 7,
            },
            [theme.breakpoints.down("sm")]: {
              paddingLeft: 4,
            },
          }}
        >
          {bodyElement}
        </Collapse>
      )}
    </Box>
  );
}
